import { Fragment, useMemo, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { BedDouble, Car, Check, Pencil, Plus, Printer, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { splitPrice, splitPriceDay, sumIsk, fmtIsk, fmtUsdApprox, fmtEurApprox, ISK_PER_USD, ISK_PER_EUR, hotelPriceForTotal, evalMoneyExpr, moneyToIsk, nameKey } from "@/lib/price";
import { DualPrice } from "@/components/DualPrice";
import { VendorInvoiceControl } from "./VendorInvoiceControl";
import { ITEM_TYPES } from "./TripView";

const STAGES = [
  { field: "ops_price", label: "Price requested", short: "Req" },
  { field: "ops_received", label: "Price received", short: "Rec" },
  { field: "ops_approved", label: "Client approved", short: "Appr" },
  { field: "ops_booking", label: "Booking sent", short: "Sent" },
  { field: "ops_confirmed", label: "Confirmed", short: "Conf" },
  { field: "ops_person", label: "Person books it themselves — price hidden from the client", short: "Books" },
  { field: "self_done", label: "I do this job myself — no vendor invoice needed, no VAT credit", short: "Myself" },
  { field: "ops_full", label: "Fully booked", short: "Full", danger: true },
  { field: "ops_info", label: "Just info", short: "Info", info: true },
];

const fmtD = (d) => {
  if (!d) return "";
  try {
    return new Date(`${d}T00:00:00`).toLocaleDateString("en-GB", { day: "numeric", month: "short" });
  } catch {
    return d;
  }
};

const dayHeading = (trip, day) => {
  if (!trip.arrival_date) return `Day ${day}`;
  const d = new Date(`${trip.arrival_date}T00:00:00`);
  d.setDate(d.getDate() + day - 1);
  return `Day ${day} — ${d.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" })}`;
};

const OpsBoxes = ({ testid, row, onToggle }) => (
  <div className="flex min-w-0 flex-wrap items-start justify-start gap-x-5 gap-y-3 sm:items-center sm:justify-end sm:gap-x-3 sm:gap-y-1">
    {/* a "just info" line has no booking workflow — only its own box shows */}
    {(row.ops_info ? STAGES.filter((s) => s.field === "ops_info") : STAGES).map((s) => {
      const on = !!row[s.field];
      const activeCls = s.danger
        ? "border-red-400 bg-red-500 text-black"
        : "border-amber-400 bg-amber-500 text-black";
      const idleHover = s.danger ? "group-hover:border-red-400/70" : "group-hover:border-amber-400/70";
      return (
        <button
          key={s.field}
          type="button"
          data-testid={`${testid}-${s.field.replace("ops_", "")}`}
          title={s.label}
          onClick={() => onToggle(s.field, !on)}
          className="group flex flex-col items-center gap-1.5 py-1 sm:flex-row sm:gap-1 sm:py-0"
          aria-pressed={on}
          aria-label={s.label}
        >
          <span
            className={`flex h-6 w-6 items-center justify-center border transition-colors sm:h-4 sm:w-4 ${
              on ? activeCls : `border-neutral-500 bg-transparent ${idleHover}`
            }`}
          >
            {on && <Check className="h-4 w-4 sm:h-3 sm:w-3" strokeWidth={3.5} />}
          </span>
          <span
            className={`text-[9px] leading-none uppercase tracking-[0.1em] transition-colors sm:text-[8px] ${
              on ? (s.danger ? "text-red-300" : "text-amber-200") : "text-neutral-500 group-hover:text-neutral-300"
            }`}
          >
            {s.short}
          </span>
        </button>
      );
    })}
  </div>
);

export const AgentSheet = ({ trip, opsUrl, headers = {}, pdfUrl, onClose, onChange, invBase = "" }) => {
  const [items, setItems] = useState(trip.items || []);
  const [hotels, setHotels] = useState(trip.hotels || []);
  const [pending, setPending] = useState("");
  const [openOnly, setOpenOnly] = useState(false);
  const [addSpot, setAddSpot] = useState(null); // { day, after } — the exact row the new moment follows
  const [addTime, setAddTime] = useState("");
  const [addTitle, setAddTitle] = useState("");
  const [addBusy, setAddBusy] = useState(false);
  const [confirmDel, setConfirmDel] = useState(""); // `${kind}:${key}` awaiting the second tap
  const [editRow, setEditRow] = useState(""); // item_id currently in inline edit mode (type, title, details)

  // two-tap delete: first tap arms it, second tap really removes the line (owner/staff choice —
  // the backend's pristine guard is skipped with force; the confirm tap is the protection)
  const removeRow = async (kind, key) => {
    const mark = `${kind}:${key}`;
    if (confirmDel !== mark) {
      setConfirmDel(mark);
      setTimeout(() => setConfirmDel((c) => (c === mark ? "" : c)), 4000);
      return;
    }
    setConfirmDel("");
    // capture everything needed to bring the line back within the undo window
    const pool = kind === "item" ? items : hotels;
    const index = pool.findIndex((x, i) => (kind === "item" ? x.item_id : x.hotel_id || String(i)) === key);
    const row = index >= 0 ? pool[index] : null;
    const linked =
      kind === "hotel" && row
        ? items.filter((i) => i.type === "hotel" && (row.name || "").trim() && (i.title || "").toLowerCase().includes((row.name || "").trim().toLowerCase()))
        : [];
    try {
      const res = await axios.post(`${opsUrl}-remove`, { kind, key, force: true }, { headers });
      setItems(res.data.items);
      setHotels(res.data.hotels);
      onChange?.({ items: res.data.items, hotels: res.data.hotels });
      toast.success("Line deleted", {
        duration: 10000,
        action: row
          ? {
              label: "Undo",
              onClick: async () => {
                try {
                  const back = await axios.post(
                    `${opsUrl}-restore`,
                    { kind, key, index: Math.max(index, 0), row, linked_items: linked },
                    { headers }
                  );
                  setItems(back.data.items);
                  setHotels(back.data.hotels);
                  onChange?.({ items: back.data.items, hotels: back.data.hotels });
                  toast.success("Line restored");
                } catch (e) {
                  toast.error(e.response?.data?.detail || "Could not restore the line.");
                }
              },
            }
          : undefined,
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not delete the line.");
    }
  };

  const delButton = (kind, key) => {
    const confirming = confirmDel === `${kind}:${key}`;
    return (
      <button
        type="button"
        data-testid={`sheet-del-${kind}-${key}`}
        onClick={() => removeRow(kind, key)}
        title={confirming ? "Tap again to really delete this line" : "Delete this line"}
        className={`flex shrink-0 items-center gap-1 border px-2 py-1 text-[9px] uppercase tracking-[0.12em] transition-colors ${
          confirming
            ? "animate-pulse border-red-400 bg-red-500 text-black"
            : "border-neutral-700 text-neutral-600 hover:border-red-400 hover:text-red-300"
        }`}
        aria-label={confirming ? "Confirm delete" : "Delete line"}
      >
        <Trash2 className="h-3 w-3" /> {confirming ? "Sure?" : ""}
      </button>
    );
  };

  const openAdd = (day, after = "") => {
    setAddSpot({ day, after });
    setAddTime("");
    setAddTitle("");
  };

  const submitAdd = async () => {
    if (!addTitle.trim() || addBusy) return;
    setAddBusy(true);
    try {
      const res = await axios.post(
        `${opsUrl}-add`,
        { day: addSpot.day, time: addTime, title: addTitle, after: addSpot.after },
        { headers }
      );
      setItems(res.data.items);
      setHotels(res.data.hotels);
      onChange?.({ items: res.data.items, hotels: res.data.hotels });
      setAddSpot(null);
      toast.success("Moment added — price and boxes are ready below");
    } catch {
      toast.error("Could not add it — please try again.");
    } finally {
      setAddBusy(false);
    }
  };

  const addForm = (spot) => (
    <div data-testid="sheet-add-form" className="border-l-2 border-amber-400/70 bg-amber-500/[0.06] py-2 pl-3 pr-2">
      <div className="flex flex-wrap items-center gap-2">
        <input
          data-testid="sheet-add-time"
          placeholder="19:00"
          value={addTime}
          onChange={(e) => setAddTime(e.target.value)}
          className="w-16 shrink-0 border border-amber-900/40 bg-[#0d0d12] px-2 py-1.5 text-[11px] text-amber-100 placeholder:text-neutral-700 outline-none focus:border-amber-400"
        />
        <input
          data-testid="sheet-add-title"
          autoFocus
          placeholder="What happens here?"
          value={addTitle}
          onChange={(e) => setAddTitle(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") submitAdd();
            if (e.key === "Escape") setAddSpot(null);
          }}
          className="min-w-40 flex-1 border border-amber-900/40 bg-[#0d0d12] px-2 py-1.5 text-[11px] text-amber-100 placeholder:text-neutral-700 outline-none focus:border-amber-400"
        />
        <button
          data-testid="sheet-add-submit"
          onClick={submitAdd}
          disabled={!addTitle.trim() || addBusy}
          className="flex items-center gap-1 bg-amber-500 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.15em] text-black transition-colors hover:bg-amber-300 disabled:opacity-40"
        >
          <Plus className="h-3 w-3" /> Add
        </button>
        <button
          data-testid="sheet-add-cancel"
          onClick={() => setAddSpot(null)}
          className="px-2 py-1.5 text-[10px] uppercase tracking-[0.15em] text-neutral-500 transition-colors hover:text-neutral-200"
        >
          Cancel
        </button>
      </div>
    </div>
  );

  // a line is "done" when confirmed with the operator, booked by the person themselves, or just information
  const isDone = (r) => r.ops_confirmed || r.ops_client_books || r.ops_person || r.ops_info;
  const vHotels = openOnly ? hotels.filter((h) => !isDone(h)) : hotels;
  const isOpenItem = (i) => !openOnly || !isDone(i);

  const days = useMemo(() => {
    const fromItems = items.filter(isOpenItem).flatMap((i) => {
      const e = i.end_day > i.day ? i.end_day : i.day;
      return Array.from({ length: e - i.day + 1 }, (_, k) => i.day + k);
    });
    // every calendar day of the journey exists in the sheet — including the departure day,
    // even when nothing is booked on it yet (it would otherwise vanish, e.g. a 6th→9th
    // trip silently dropping the 9th)
    const calDays = [];
    if (trip.arrival_date && trip.departure_date) {
      const n = Math.round((new Date(`${trip.departure_date}T00:00:00`) - new Date(`${trip.arrival_date}T00:00:00`)) / 86400000) + 1;
      for (let d = 1; d <= Math.max(n, 1); d++) calDays.push(d);
    }
    return [...new Set([...fromItems, ...calDays])].sort((a, b) => a - b);
  }, [items, openOnly, trip.arrival_date, trip.departure_date]);
  const rentals = trip.rentals || [];
  // A hotel-type line (check-in/out, plain hotel line) mirrors its stay — accent-insensitive,
  // so "Check-in — Kría Hotel" finds the "Kría Hótel" stay
  const hotelForItem = (item) => {
    if (item.type !== "hotel") return null;
    const t = nameKey(item.title);
    return (
      hotels.find((h) => {
        const hn = nameKey(h.name);
        if (!hn) return false;
        const first = hn.split(/\s+/)[0];
        return t.includes(hn) || (first.length > 3 && t.includes(first));
      }) || null
    );
  };
  const allRows = useMemo(() => [...hotels, ...items], [hotels, items]);
  const doneCount = allRows.filter((r) => r.ops_confirmed).length;
  const openCount = allRows.filter((r) => !isDone(r)).length;
  // a rental counts once — its total price; stay-linked lines never count a copy of their own
  const totalIsk = useMemo(() => sumIsk([...hotels.map(hotelPriceForTotal), ...items.filter((r) => !hotelForItem(r)).map((r) => r.price), ...rentals.map((r) => r.price)]), [hotels, items, rentals]);

  const hotelNights = (h) => {
    if (h.check_in && h.check_out) {
      const n = Math.round((new Date(`${h.check_out}T00:00:00`) - new Date(`${h.check_in}T00:00:00`)) / 86400000);
      if (n > 0) return n;
    }
    return parseInt(h.nights, 10) || 0;
  };

  const splitToggle = (kind, key, on, n, unit, testid) => (
    <button
      type="button"
      data-testid={testid}
      aria-pressed={on}
      title={`Divide the total across the ${n} charged ${unit}s — the return day is free`}
      onClick={() => toggle(kind, key, "split_price", !on)}
      className={`flex h-6 shrink-0 items-center gap-1 border px-1.5 text-[9px] uppercase tracking-[0.1em] transition-colors ${
        on ? "border-amber-400 bg-amber-500/15 text-amber-200" : "border-neutral-700 text-neutral-500 hover:border-amber-400/60 hover:text-amber-200"
      }`}
    >
      ÷ {n} {unit}
    </button>
  );

  const toggle = async (kind, key, field, value) => {
    const mark = `${kind}:${key}:${field}`;
    if (pending === mark) return;
    // optimistic
    if (kind === "item") setItems((xs) => xs.map((x) => (x.item_id === key ? { ...x, [field]: value } : x)));
    else setHotels((xs) => xs.map((x, i) => ((x.hotel_id || String(i)) === key ? { ...x, [field]: value } : x)));
    setPending(mark);
    try {
      const res = await axios.patch(opsUrl, { kind, key, field, value }, { headers });
      setItems(res.data.items);
      setHotels(res.data.hotels);
      onChange?.({ items: res.data.items, hotels: res.data.hotels });
    } catch {
      toast.error("Could not save — please try again.");
      if (kind === "item") setItems((xs) => xs.map((x) => (x.item_id === key ? { ...x, [field]: !value } : x)));
      else setHotels((xs) => xs.map((x, i) => ((x.hotel_id || String(i)) === key ? { ...x, [field]: !value } : x)));
    } finally {
      setPending("");
    }
  };

  const commitText = async (kind, key, field, value, prev, msg) => {
    // price boxes act as calculators — "20.000*4" stores as 50.000 so sheet, builder, demo and totals agree
    const v = field === "price" ? (evalMoneyExpr(value) ?? value.trim()) : value.trim();
    if (v === (prev || "")) return;
    try {
      const res = await axios.patch(opsUrl, { kind, key, field, value: v }, { headers });
      setItems(res.data.items);
      setHotels(res.data.hotels);
      onChange?.({ items: res.data.items, hotels: res.data.hotels });
      toast.success(msg);
      // a freshly written alternative on a fully-booked row becomes its own bookable line,
      // with the same time slot / stay dates and all the boxes ready
      if (field === "alternative" && v && !(prev || "").trim()) {
        try {
          let addBody;
          if (kind === "item") {
            const src = res.data.items.find((x) => x.item_id === key);
            addBody = { day: src?.day || 1, time: src?.time || "", title: v, after: key, kind: "item", alt_of: key };
          } else {
            const src = res.data.hotels.find((h, i) => (h.hotel_id || String(i)) === key);
            addBody = {
              title: v,
              kind: "hotel",
              after: src?.hotel_id || "",
              alt_of: key,
              stay: {
                check_in: src?.check_in || "",
                check_out: src?.check_out || "",
                nights: src?.nights || "",
                rooms: src?.rooms || "",
                room_type: src?.room_type || "double",
              },
            };
          }
          const addRes = await axios.post(`${opsUrl}-add`, addBody, { headers });
          setItems(addRes.data.items);
          setHotels(addRes.data.hotels);
          onChange?.({ items: addRes.data.items, hotels: addRes.data.hotels });
          toast.success("Alternative added as a new line — book it there");
        } catch {
          toast.error("Alternative saved, but the new line could not be created.");
        }
      }
      // erasing the alternative also takes out its line — unless it already has prices or ticks
      if (field === "alternative" && !v && (prev || "").trim()) {
        const pool = kind === "item" ? res.data.items : res.data.hotels;
        const linked = pool.find((x) => x.alt_of === key);
        if (linked) {
          try {
            const rm = await axios.post(
              `${opsUrl}-remove`,
              { kind, key: kind === "item" ? linked.item_id : linked.hotel_id },
              { headers }
            );
            setItems(rm.data.items);
            setHotels(rm.data.hotels);
            onChange?.({ items: rm.data.items, hotels: rm.data.hotels });
            toast.success("Alternative line removed too");
          } catch (e) {
            if (e.response?.status === 409) toast.error("Alternative cleared — its line stays because it already has prices or ticks");
            else toast.error("Alternative cleared, but the line could not be removed.");
          }
        }
      }
    } catch {
      toast.error("Could not save — please try again.");
    }
  };

  const priceInput = (kind, key, value) => (
    <input
      key={`${key}:${value || ""}`}
      data-testid={`price-${kind}-${key}`}
      defaultValue={value || ""}
      placeholder="Price"
      title="Full price — copied everywhere the client sees it · math works here: 20.000*4 calculates when you leave the field"
      onKeyDown={(e) => {
        if (e.key === "Enter") e.target.blur();
      }}
      onBlur={(e) => commitText(kind, key, "price", e.target.value, value, "Price updated — shows everywhere the client sees it")}
      className="w-24 shrink-0 border border-amber-900/40 bg-[#0d0d12] px-2 py-1 text-[10px] uppercase tracking-[0.12em] text-amber-100 placeholder:text-neutral-700 outline-none transition-colors focus:border-amber-400"
    />
  );

  const assigneeInput = (kind, key, value) => (
    <input
      key={`who-${key}:${value || ""}`}
      data-testid={`assignee-${kind}-${key}`}
      defaultValue={value || ""}
      placeholder="Who?"
      title="Who is doing this job"
      onKeyDown={(e) => {
        if (e.key === "Enter") e.target.blur();
      }}
      onBlur={(e) => commitText(kind, key, "assignee", e.target.value, value, "Assignee saved")}
      className="w-20 shrink-0 border border-neutral-700 bg-[#0d0d12] px-2 py-1 text-[10px] text-neutral-200 placeholder:text-neutral-700 outline-none transition-colors focus:border-amber-400"
    />
  );

  return (
    <motion.div
      data-testid="agent-sheet"
      className="fixed inset-0 z-[95] overflow-y-auto bg-black/90 backdrop-blur-sm"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <div className="mx-auto max-w-5xl px-4 py-6 sm:px-6">
        <div className="flex items-center justify-between border border-amber-500/40 bg-amber-500/10 px-4 py-2.5">
          <p className="text-[10px] font-semibold uppercase tracking-[0.3em] text-amber-200">
            Agent sheet <span className="hidden sm:inline">— tick each step as you go · saves instantly</span>
          </p>
          <div className="flex items-center gap-3">
            <button
              data-testid="sheet-filter-open"
              onClick={() => setOpenOnly((v) => !v)}
              aria-pressed={openOnly}
              title="Hide lines that are confirmed or booked by the client — review only what is still open"
              className={`flex items-center gap-1.5 border px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.2em] transition-colors ${
                openOnly ? "border-amber-400 bg-amber-500 text-black" : "border-amber-500/50 text-amber-200 hover:bg-amber-500 hover:text-black"
              }`}
            >
              {openOnly ? `Open only · ${openCount}` : "Open only"}
            </button>
            {pdfUrl && (
              <button
                data-testid="agent-sheet-print"
                onClick={async () => {
                  try {
                    const res = await axios.get(pdfUrl, { headers, responseType: "blob" });
                    const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
                    const a = document.createElement("a");
                    a.href = url;
                    a.download = `agent-sheet-${(trip.client?.name || "trip").replace(/\s+/g, "-").toLowerCase()}.pdf`;
                    a.click();
                    URL.revokeObjectURL(url);
                  } catch {
                    toast.error("Could not build the printable sheet.");
                  }
                }}
                className="flex items-center gap-1.5 border border-amber-500/50 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.2em] text-amber-200 transition-colors hover:bg-amber-500 hover:text-black"
              >
                <Printer className="h-3.5 w-3.5" /> Print
              </button>
            )}
            <button data-testid="agent-sheet-close" onClick={onClose} className="text-amber-200 transition-colors hover:text-white" aria-label="Close agent sheet">
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="border border-amber-500/20 bg-[#070709] px-4 py-5 sm:px-8 sm:py-6">
          <div className="flex flex-wrap items-baseline justify-between gap-2 border-b border-amber-500/15 pb-3">
            <div>
              <h2 className="font-luxe text-xl uppercase tracking-[0.12em] text-neutral-50 sm:text-2xl">{trip.client?.name}</h2>
              <p className="mt-0.5 text-[10px] uppercase tracking-[0.2em] text-neutral-500">
                {fmtD(trip.arrival_date)} → {fmtD(trip.departure_date)} · {trip.pax} pax
                {trip.location ? ` · ${trip.location}` : ""}
              </p>
            </div>
            <div className="text-right">
              <p data-testid="agent-sheet-progress" className="text-[10px] uppercase tracking-[0.2em] text-amber-200/90">
                {doneCount} / {allRows.length} confirmed
              </p>
              {totalIsk > 0 && (
                <p data-testid="sheet-total" className="mt-1 font-luxe text-sm tracking-wide text-amber-100">
                  Trip total {fmtIsk(totalIsk)}{" "}
                  <span className="text-[10px] font-normal normal-case tracking-normal text-neutral-500">{trip.client_region === "eu" ? fmtEurApprox(totalIsk / ISK_PER_EUR) : fmtUsdApprox(totalIsk / ISK_PER_USD)}</span>
                </p>
              )}
            </div>
          </div>

          {hotels.some((h) => !openOnly || !isDone(h)) && (
            <div className="mt-3">
              <p className="flex items-center gap-2 text-[9px] font-semibold uppercase tracking-[0.28em] text-amber-300/80">
                <BedDouble className="h-3 w-3" /> Stays
              </p>
              <div className="mt-1 space-y-2">
                {hotels.map((h, i) => {
                  if (openOnly && isDone(h)) return null;
                  const key = h.hotel_id || String(i);
                  const nights = hotelNights(h);
                  const share = h.split_price && nights > 0 ? splitPrice(h.price, nights) : "";
                  const meta = [
                    h.check_in ? `${fmtD(h.check_in)} → ${fmtD(h.check_out)}` : "",
                    h.rooms ? `${h.rooms} × ${h.room_type}` : "",
                    share ? "÷ per night" : "",
                  ].filter(Boolean).join(" · ");
                  return (
                    <div key={key} data-testid={`sheet-stay-${key}`} className={`border-l-2 py-2 pl-3 pr-2 ${h.ops_info ? "border-neutral-700/70 bg-white/[0.015]" : "border-amber-500/25 bg-amber-500/[0.03]"}`}>
                      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
                        <p className={`min-w-0 font-serif text-[15px] leading-snug max-sm:w-full ${h.ops_full ? "text-red-300/80 line-through" : "text-neutral-50"}`}>{h.name}</p>
                        <div className="ml-auto flex shrink-0 items-center gap-2">
                          {!h.ops_info && share && <DualPrice value={share} className="text-[10px] uppercase tracking-[0.12em] text-amber-200/80" />}
                          {!h.ops_info && priceInput("hotel", key, h.price)}
                          {!h.ops_info && nights > 1 && splitToggle("hotel", key, !!h.split_price, nights, "night", `split-hotel-${key}`)}
                        </div>
                      </div>
                      {h.ops_full && <p className="mt-0.5 text-[9px] uppercase tracking-[0.16em] text-red-400/90">Fully booked — find an alternative</p>}
                      <div className="mt-1 flex flex-wrap items-center justify-between gap-x-3 gap-y-1.5">
                        <p className="text-[10px] uppercase tracking-[0.16em] text-neutral-500">{meta}</p>
                        <div className="flex flex-wrap items-center justify-start gap-x-3 gap-y-1.5 sm:justify-end">
                          {assigneeInput("hotel", key, h.assignee)}
                          {invBase && !h.ops_info && !h.ops_full && (
                            <VendorInvoiceControl
                              base={invBase}
                              headers={headers}
                              kind="hotel"
                              rowKey={key}
                              row={h}
                              onField={(f, v) => toggle("hotel", key, f, v)}
                              onRows={(items2, hotels2) => {
                                setItems(items2);
                                setHotels(hotels2);
                                onChange?.({ items: items2, hotels: hotels2 });
                              }}
                              testid={`inv-hotel-${key}`}
                            />
                          )}
                          <OpsBoxes testid={`ops-hotel-${key}`} row={h} onToggle={(f, v) => toggle("hotel", key, f, v)} />
                          {delButton("hotel", key)}
                        </div>
                      {h.ops_full && (
                        <div className="mt-1.5 flex items-center gap-2">
                          <span className="shrink-0 text-[9px] uppercase tracking-[0.14em] text-red-400/90">Alt</span>
                          <input
                            key={`alt-${key}:${h.alternative || ""}`}
                            data-testid={`alt-hotel-${key}`}
                            defaultValue={h.alternative || ""}
                            placeholder="Write the alternative here"
                            title="Alternative to offer the client for this fully-booked stay"
                            onKeyDown={(e) => {
                              if (e.key === "Enter") e.target.blur();
                            }}
                            onBlur={(e) => commitText("hotel", key, "alternative", e.target.value, h.alternative, "Alternative saved")}
                            className="min-w-0 flex-1 border border-red-900/50 bg-[#0d0d12] px-2 py-1 text-[10px] text-red-100 placeholder:text-neutral-700 outline-none transition-colors focus:border-red-400"
                          />
                        </div>
                      )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {days.map((day) => (
            <div key={day} className="mt-5">
              <div className="flex items-center justify-between gap-2 border-b border-amber-500/20 pb-1">
                <p data-testid={`sheet-day-${day}`} className="font-serif text-[15px] italic leading-snug text-amber-200">
                  {dayHeading(trip, day)}
                </p>
                <button
                  data-testid={`sheet-add-day-${day}`}
                  onClick={() => openAdd(day)}
                  title="Add a moment at the end of this day"
                  className="flex items-center gap-1 px-2 py-0.5 text-[9px] uppercase tracking-[0.18em] text-amber-300/70 transition-colors hover:text-amber-200"
                >
                  <Plus className="h-3 w-3" /> Add
                </button>
              </div>
              {addSpot && addSpot.day === day && !addSpot.after && <div className="mt-2">{addForm(addSpot)}</div>}
              <div className="mt-1 space-y-2">
                {rentals
                  .filter((r) => r.name && (r.from_day || 1) <= day && day <= (r.to_day || r.from_day || 1))
                  .map((r) => {
                    const rFrom = r.from_day || 1;
                    const rSpan = Math.max(1, (r.to_day || rFrom) - rFrom + 1);
                    const rTotal = moneyToIsk(r.price);
                    const rShare = rTotal ? Math.round(rTotal / rSpan) : 0;
                    return (
                      <div
                        key={`rental-${r.rental_id || r.name}-${day}`}
                        data-testid={`sheet-rental-${r.rental_id || r.name}-d${day}`}
                        className="border-l-2 border-neutral-600/70 bg-white/[0.02] py-2 pl-3 pr-2"
                      >
                        <p className="flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] text-neutral-400">
                          <Car className="h-3 w-3 shrink-0 text-amber-300/70" />
                          <span className="min-w-0 flex-1">Rental — {r.name}</span>
                          {rShare > 0 && <span className="shrink-0 font-luxe tracking-[0.12em] text-amber-200/90">{fmtIsk(rShare)} / day</span>}
                        </p>
                      </div>
                    );
                  })}
                {items.filter((i) => isOpenItem(i) && i.day <= day && (i.end_day > i.day ? i.end_day : i.day) >= day).map((item) => {
                  const endDay = item.end_day > item.day ? item.end_day : item.day;
                  const chargedN = endDay - item.day;
                  const isFirst = day === item.day;
                  const isReturnDay = item.split_price && chargedN > 0 && day === endDay;
                  const share = item.split_price && chargedN > 0 && day < endDay ? splitPriceDay(item.price, chargedN, day - item.day + 1) : "";
                  const hotelSrc = hotelForItem(item);
                  const hn = hotelSrc ? hotelNights(hotelSrc) : 0;
                  // check-in row shows night 1's share, check-out row the last night (absorbs rounding)
                  const isCheckout = String(item.auto || "").endsWith("-out") || /check-out/i.test(item.title || "");
                  const hotelShare = hotelSrc?.split_price && hn > 0 ? splitPriceDay(hotelSrc.price, hn, isCheckout ? hn : 1) : "";
                  return (
                    <Fragment key={`${item.item_id}-${day}`}>
                    <div data-testid={isFirst ? `sheet-item-${item.item_id}` : `sheet-item-${item.item_id}-d${day}`} className={`border-l-2 py-2 pl-3 pr-2 ${item.ops_info ? "border-neutral-700/70 bg-white/[0.015]" : "border-amber-500/25 bg-amber-500/[0.03]"}`}>
                      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
                        <div className="flex min-w-0 items-baseline gap-2.5 max-sm:w-full sm:flex-1">
                          {isFirst ? (
                            <input
                              key={`time-${item.item_id}:${item.time || ""}`}
                              data-testid={`time-item-${item.item_id}`}
                              defaultValue={item.time || ""}
                              placeholder="—"
                              title="Time of day — e.g. 19:00 · saves when you leave the field"
                              onKeyDown={(e) => {
                                if (e.key === "Enter") e.target.blur();
                              }}
                              onBlur={(e) => commitText("item", item.item_id, "time", e.target.value, item.time, "Time updated")}
                              className="w-11 shrink-0 border border-transparent bg-transparent px-0 py-0 text-[10px] tracking-[0.1em] text-amber-200/80 outline-none transition-colors placeholder:text-neutral-700 hover:border-amber-900/60 focus:border-amber-400"
                            />
                          ) : (
                            <span className="w-11 shrink-0 text-[10px] tracking-[0.1em] text-amber-200/80">{item.time || "—"}</span>
                          )}
                          {isFirst && editRow === item.item_id ? (
                            <span className="flex min-w-0 flex-1 flex-wrap items-center gap-1.5">
                              <select
                                data-testid={`sheet-item-type-${item.item_id}`}
                                value={ITEM_TYPES.some((t) => t.value === item.type) ? item.type : "activity"}
                                onChange={(e) => toggle("item", item.item_id, "type", e.target.value)}
                                title="What kind of moment this is"
                                className="shrink-0 border border-amber-900/40 bg-[#0d0d12] px-1 py-1 text-[9px] uppercase tracking-[0.1em] text-neutral-300 outline-none focus:border-amber-400"
                              >
                                {ITEM_TYPES.map((t) => (
                                  <option key={t.value} value={t.value}>{t.label}</option>
                                ))}
                              </select>
                              <input
                                key={`title-${item.item_id}:${item.title}`}
                                data-testid={`sheet-item-title-${item.item_id}`}
                                defaultValue={item.title}
                                autoFocus
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") e.target.blur();
                                }}
                                onBlur={(e) => commitText("item", item.item_id, "title", e.target.value, item.title, "Line updated")}
                                className="min-w-0 flex-1 border border-amber-900/40 bg-[#0d0d12] px-2 py-1 text-sm text-neutral-100 outline-none focus:border-amber-400"
                              />
                              <button
                                type="button"
                                data-testid={`sheet-item-done-${item.item_id}`}
                                onClick={() => setEditRow("")}
                                className="flex shrink-0 items-center gap-1 border border-emerald-500/50 px-2 py-1 text-[9px] uppercase tracking-[0.12em] text-emerald-200 transition-colors hover:bg-emerald-500 hover:text-black"
                              >
                                <Check className="h-3 w-3" /> Done
                              </button>
                            </span>
                          ) : (
                            <button
                              type="button"
                              data-testid={`sheet-item-edit-${item.item_id}`}
                              onClick={() => isFirst && setEditRow(item.item_id)}
                              title={isFirst ? "Tap to edit this line — type, title and details" : undefined}
                              className={`min-w-0 text-left ${isFirst ? "cursor-text" : "cursor-default"}`}
                            >
                              <p className={`text-sm leading-snug ${item.ops_full ? "text-red-300/80 line-through" : "text-neutral-100"}`}>
                                {item.title}
                                {!isFirst && <span className="ml-2 text-[9px] uppercase tracking-[0.14em] text-neutral-600">day {day - item.day + 1} of {endDay - item.day + 1}</span>}
                                {isFirst && <Pencil className="mb-0.5 ml-1.5 inline h-3 w-3 text-neutral-700" />}
                              </p>
                            </button>
                          )}
                        </div>
                        <div className="ml-auto flex shrink-0 items-center gap-2">
                          {isFirst ? (
                            !item.ops_info ? (
                              <>
                                {share && <DualPrice value={share} className="text-[10px] uppercase tracking-[0.12em] text-amber-200/80" />}
                                {hotelSrc
                                  ? priceInput("hotel", hotelSrc.hotel_id || String(hotels.indexOf(hotelSrc)), hotelPriceForTotal(hotelSrc))
                                  : priceInput("item", item.item_id, item.price)}
                                {!hotelSrc && chargedN > 0 && splitToggle("item", item.item_id, !!item.split_price, chargedN, "day", `split-item-${item.item_id}`)}
                              </>
                            ) : null
                          ) : isReturnDay ? (
                            <span className="text-[9px] uppercase tracking-[0.14em] text-neutral-600">Return — no charge</span>
                          ) : share ? (
                            <span className="text-[10px] uppercase tracking-[0.12em] text-amber-200/80"><DualPrice value={share} /> / day</span>
                          ) : hotelShare && !isFirst ? (
                            <span className="text-[10px] uppercase tracking-[0.12em] text-amber-200/80"><DualPrice value={hotelShare} /> / night</span>
                          ) : null}
                        </div>
                      </div>
                      {isFirst && editRow === item.item_id ? (
                        <textarea
                          key={`details-${item.item_id}:${item.details || ""}`}
                          data-testid={`sheet-item-details-${item.item_id}`}
                          defaultValue={item.details || ""}
                          rows={2}
                          placeholder="Details — logistics, contact, what's included (the client sees this)"
                          onBlur={(e) => commitText("item", item.item_id, "details", e.target.value, item.details || "", "Details updated")}
                          className="mt-1.5 w-full border border-amber-900/40 bg-[#0d0d12] px-2 py-1.5 text-xs leading-relaxed text-neutral-300 placeholder:text-neutral-700 outline-none focus:border-amber-400 sm:ml-14 sm:w-[calc(100%-3.5rem)]"
                        />
                      ) : item.details ? (
                        <p data-testid={`sheet-item-details-view-${item.item_id}`} className="mt-0.5 line-clamp-2 text-xs leading-relaxed text-neutral-500 sm:pl-14">
                          {item.details}
                        </p>
                      ) : null}
                      {item.ops_full && <p className="mt-0.5 text-[9px] uppercase tracking-[0.16em] text-red-400/90 sm:pl-14">Fully booked — find an alternative</p>}
                      <div className="mt-1 flex flex-wrap items-center justify-start gap-x-3 gap-y-1.5 sm:justify-end sm:pl-14">
                        {isFirst && assigneeInput("item", item.item_id, item.assignee)}
                        {isFirst && (
                          <button
                            data-testid={`sheet-add-after-${item.item_id}`}
                            onClick={() => openAdd(item.day, item.item_id)}
                            title="Add a moment right after this one"
                            className="flex items-center gap-1 border border-amber-500/40 px-2 py-1 text-[9px] uppercase tracking-[0.15em] text-amber-200/80 transition-colors hover:bg-amber-500 hover:text-black"
                          >
                            <Plus className="h-3 w-3" /> Add here
                          </button>
                        )}
                        {isFirst && invBase && !item.ops_info && !item.ops_full && (
                          <VendorInvoiceControl
                            base={invBase}
                            headers={headers}
                            kind="item"
                            rowKey={item.item_id}
                            row={item}
                            onField={(f, v) => toggle("item", item.item_id, f, v)}
                            onRows={(items2, hotels2) => {
                              setItems(items2);
                              setHotels(hotels2);
                              onChange?.({ items: items2, hotels: hotels2 });
                            }}
                            testid={`inv-item-${item.item_id}`}
                          />
                        )}
                        <OpsBoxes testid={isFirst ? `ops-item-${item.item_id}` : `ops-item-${item.item_id}-d${day}`} row={item} onToggle={(f, v) => toggle("item", item.item_id, f, v)} />
                        {isFirst && delButton("item", item.item_id)}
                      </div>
                      {isFirst && item.ops_full && (
                        <div className="mt-1.5 flex items-center gap-2 sm:pl-14">
                          <span className="shrink-0 text-[9px] uppercase tracking-[0.14em] text-red-400/90">Alt</span>
                          <input
                            key={`alt-${item.item_id}:${item.alternative || ""}`}
                            data-testid={`alt-item-${item.item_id}`}
                            defaultValue={item.alternative || ""}
                            placeholder="Write the alternative here"
                            title="Alternative to offer the client for this fully-booked moment"
                            onKeyDown={(e) => {
                              if (e.key === "Enter") e.target.blur();
                            }}
                            onBlur={(e) => commitText("item", item.item_id, "alternative", e.target.value, item.alternative, "Alternative saved")}
                            className="min-w-0 flex-1 border border-red-900/50 bg-[#0d0d12] px-2 py-1 text-[10px] text-red-100 placeholder:text-neutral-700 outline-none transition-colors focus:border-red-400"
                          />
                        </div>
                      )}
                    </div>
                    {addSpot && addSpot.after === item.item_id && addForm(addSpot)}
                    </Fragment>
                  );
                })}
              </div>
            </div>
          ))}

          {allRows.length === 0 && (
            <p className="mt-6 border border-dashed border-neutral-700 p-5 text-center text-xs text-neutral-500">
              No stays or moments on this itinerary yet.
            </p>
          )}

          <div className="mt-4 hidden justify-end gap-4 border-t border-amber-500/15 pt-3 sm:flex">
            {STAGES.map((s) => (
              <p key={s.field} className="text-[9px] uppercase tracking-[0.18em] text-neutral-500">
                {s.label}
              </p>
            ))}
          </div>
        </div>
      </div>
    </motion.div>
  );
};
